import { db } from '../config';
import { collection, getDocs, doc, setDoc, query, limit, orderBy, where, startAfter, GeoPoint } from 'firebase/firestore';

/**
 * Utility function to migrate existing sites from 'sites' to 'sitesearch_beta' collection
 * This helps ensure all sites get the optimized data structure for 1000+ sites support
 */
export const migrateSitesToBeta = async (batchSize = 100, statusCallback) => {
  try {
    // Start tracking stats
    const stats = {
      total: 0,
      processed: 0,
      success: 0,
      error: 0,
      skipped: 0,
      mediaProcessed: 0
    };
    
    if (typeof statusCallback === 'function') {
      statusCallback({
        status: 'started',
        message: 'Starting migration...',
        stats
      });
    }
    
    // Get the count of all sites
    const countQuery = collection(db, 'sites');
    const countSnapshot = await getDocs(countQuery);
    stats.total = countSnapshot.size;
    
    if (typeof statusCallback === 'function') {
      statusCallback({
        status: 'counting',
        message: `Found ${stats.total} sites to process`,
        stats
      });
    }

    // Process in batches to avoid memory issues
    let lastDoc = null;
    let hasMoreDocs = true;
    
    while (hasMoreDocs) {
      // Construct query for the current batch
      let sitesQuery;
      if (lastDoc) {
        sitesQuery = query(
          collection(db, 'sites'),
          orderBy('updatedAt', 'desc'),
          limit(batchSize),
          startAfter(lastDoc)
        );
      } else {
        sitesQuery = query(
          collection(db, 'sites'),
          orderBy('updatedAt', 'desc'),
          limit(batchSize)
        );
      }
      
      // Get the sites for this batch
      const sitesSnapshot = await getDocs(sitesQuery);
      
      if (sitesSnapshot.empty) {
        hasMoreDocs = false;
        continue;
      }
      
      lastDoc = sitesSnapshot.docs[sitesSnapshot.docs.length - 1];
      
      // Get all media for these sites at once
      const siteIds = sitesSnapshot.docs.map(doc => doc.id);
      const mediaQuery = query(
        collection(db, 'site_media'),
        where('siteId', 'in', siteIds)
      );
      const mediaSnapshot = await getDocs(mediaQuery);
      
      // Group media by site ID
      const mediaBySiteId = {};
      mediaSnapshot.forEach(mediaDoc => {
        const data = mediaDoc.data();
        const siteId = data.siteId;
        if (!mediaBySiteId[siteId]) {
          mediaBySiteId[siteId] = [];
        }
        mediaBySiteId[siteId].push({
          id: mediaDoc.id,
          ...data
        });
      });
      
      stats.mediaProcessed += mediaSnapshot.size;
      
      // Process each site in the batch - this would normally be done by the Cloud Function
      // but we're manually triggering for existing sites
      for (const siteDoc of sitesSnapshot.docs) {
        try {
          stats.processed++;
          
          const siteId = siteDoc.id;
          const siteData = siteDoc.data();
          
          // Get media for this site
          const mediaItems = mediaBySiteId[siteId] || [];
          
          // Create a GeoPoint for coordinates if they exist
          let geoPoint = null;
          if (siteData.location?.coordinates?.lat && siteData.location?.coordinates?.lng) {
            try {
              const lat = Number(siteData.location.coordinates.lat);
              const lng = Number(siteData.location.coordinates.lng);
              if (!isNaN(lat) && !isNaN(lng)) {
                geoPoint = new GeoPoint(lat, lng);
              }
            } catch (coordError) {
              console.error(`Error creating GeoPoint for site ${siteId}:`, coordError);
            }
          }
          
          // Function to normalize feature IDs consistently
          const normalizeFeature = (featureId) => {
            const str = String(featureId || '');
            return str.toLowerCase()
              .replace(/[^a-z0-9]+/g, '')
              .trim();
          };
          
          // Get all unique normalized features from all media
          const allNormalizedFeatures = new Set();
          // Prepare feature index
          const featureIndex = {};
          
          mediaItems.forEach(media => {
            if (!Array.isArray(media.predictions)) return;
            
            media.predictions.forEach(pred => {
              if (pred.source === 'auto' && pred.confidence <= 0.5) return;
              
              const normalizedId = normalizeFeature(pred.class);
              if (!normalizedId) return;
              
              allNormalizedFeatures.add(normalizedId);
              
              if (!featureIndex[normalizedId]) {
                featureIndex[normalizedId] = {
                  count: 0,
                  mediaIds: [],
                  confidence: 0
                };
              }
              
              featureIndex[normalizedId].count++;
              featureIndex[normalizedId].mediaIds.push(media.id);
              featureIndex[normalizedId].confidence += (pred.confidence || 0.75);
            });
          });
          
          // Calculate average confidence for each feature
          Object.keys(featureIndex).forEach(feature => {
            featureIndex[feature].confidence = 
              featureIndex[feature].confidence / featureIndex[feature].count;
          });
          
          // Prepare map preview media
          const mapPreviewMedia = mediaItems
            .filter(media => media.type === 'image' && media.url)
            .slice(0, 3)
            .map(media => ({
              id: media.id,
              url: media.url,
              featureTags: Array.isArray(media.predictions) 
                ? media.predictions
                  .filter(p => !(p.source === 'auto' && p.confidence <= 0.5))
                  .map(p => normalizeFeature(p.class))
                  .filter(Boolean)
                : []
            }));
          
          // Get main image
          const mainImage = mediaItems.find(media => media.type === 'image' && media.url) || null;
          
          // Prepare other media (limited count)
          const MAX_IMAGES = 5;
          const otherMedia = mediaItems
            .filter(media => media.type === 'image' && media.url && media.id !== mainImage?.id)
            .slice(0, MAX_IMAGES)
            .map(media => ({
              id: media.id,
              url: media.url,
              type: media.type,
              predictions: media.predictions || [],
              thumbnailUrl: media.thumbnailUrl || null
            }));
          
          // Calculate map priority score
          const mapPriority = (() => {
            let score = 0;
            
            // Sites with more media get priority
            score += Math.min(mediaItems.length, 20) * 2;
            
            // Featured sites get top priority
            if (siteData.featured) score += 100;
            
            // Sites with complete data get higher priority
            if (siteData.description && siteData.description.length > 100) score += 20;
            if (siteData.culture) score += 10;
            if (siteData.startEra || siteData.endEra) score += 10;
            
            // Recently updated sites get higher priority
            const updateTime = siteData.updatedAt?.toMillis ? siteData.updatedAt.toMillis() : 0;
            if (updateTime > 0) {
              const now = Date.now();
              const daysSinceUpdate = Math.floor((now - updateTime) / (1000 * 60 * 60 * 24));
              if (daysSinceUpdate < 30) {
                score += Math.max(0, 30 - daysSinceUpdate);
              }
            }
            
            return score;
          })();
          
          // Prepare zoom level data
          const zoomLevelData = {
            high: {
              name: siteData.name,
              culture: siteData.culture || '',
              era: siteData.startEra || siteData.estimatedEra || '',
              mainImageUrl: mainImage?.url || null,
              featureCount: allNormalizedFeatures.size,
              hasSublocations: false // Will be updated when sublocations are processed
            },
            medium: {
              name: siteData.name
            },
            low: {
              id: siteData.id
            }
          };
          
          // Prepare optimized beta search document
          const searchBetaData = {
            // Basic site info
            id: siteId,
            name: siteData.name || '',
            description: siteData.description || '',
            location: {
              address: siteData.location?.address || '',
              coordinates: geoPoint,
              region: siteData.location?.region || '',
              country: siteData.location?.country || ''
            },
            
            // Map-specific data
            mapPriority: mapPriority,
            mapPreviewMedia: mapPreviewMedia,
            sublocationCount: 0, // Will be updated when sublocations are processed
            mapSublocations: [], // Will be populated when sublocations are processed
            zoomLevelData: zoomLevelData,
            
            // Media data
            mainImageUrl: mainImage?.url || null,
            mainImagePredictions: mainImage?.predictions || [],
            media: otherMedia,
            mediaCount: mediaItems.length,
            
            // Feature data - enhanced for 1000+ sites
            features_normalized: Array.from(allNormalizedFeatures),
            featureCount: allNormalizedFeatures.size,
            featureIndex: featureIndex,
            
            // Feature-media mapping for quick filtering without loading all media
            featureMediaMap: Object.fromEntries(
              Object.entries(featureIndex).map(([feature, data]) => 
                [feature, data.mediaIds.slice(0, 3)]
              )
            ),
            
            // Enhanced zoom-specific feature data
            zoomFeatureData: {
              low: {
                hasFeatures: allNormalizedFeatures.size > 0
              },
              medium: {
                featureCount: allNormalizedFeatures.size,
                topFeatures: Object.entries(featureIndex)
                  .sort((a, b) => b[1].confidence - a[1].confidence)
                  .slice(0, 3)
                  .map(([feature]) => feature)
              }
            },
            
            // Basic metadata
            culture: siteData.culture || '',
            startEra: siteData.startEra || '',
            endEra: siteData.endEra || '',
            featured: !!siteData.featured,
            
            // Timestamps
            createdAt: siteData.createdAt || new Date(),
            updatedAt: new Date(),
            
            // Search-related metadata
            hasDetails: !!(siteData.description && siteData.description.length > 100),
            lastMediaUpdate: siteData.lastMediaUpdate || null
          };
          
          // Add geohash field for efficient geospatial queries if coordinates exist
          if (geoPoint) {
            try {
              const lat = geoPoint.latitude;
              const lng = geoPoint.longitude;
              const precision = 5; // ~5km precision
              
              // Generate simple quadrant identifiers for grid-based querying
              const latGrid = Math.floor((lat + 90) / 180 * Math.pow(2, precision));
              const lngGrid = Math.floor((lng + 180) / 360 * Math.pow(2, precision));
              
              searchBetaData.geoHash = `${latGrid}:${lngGrid}`;
              
              // Add regional clustering identifiers
              searchBetaData.regionCode = `${Math.floor(lat)}:${Math.floor(lng)}`;
              searchBetaData.clusterGroup = `${Math.floor(lat/10)}:${Math.floor(lng/10)}`;
            } catch (error) {
              console.error(`Error generating geohash for site ${siteId}:`, error);
            }
          }
          
          // Get sublocations for this site
          try {
            const sublocationsQuery = query(
              collection(db, 'site_sublocations'),
              where('siteId', '==', siteId)
            );
            
            const sublocationsSnapshot = await getDocs(sublocationsQuery);
            
            if (!sublocationsSnapshot.empty) {
              // Process sublocations
              const sublocations = [];
              
              sublocationsSnapshot.forEach(sublocationDoc => {
                const sublocationData = sublocationDoc.data();
                
                // Only include sublocations with coordinates
                if (sublocationData.coordinates?.lat && sublocationData.coordinates?.lng) {
                  try {
                    // Create a compact version of the sublocation for the map
                    const sublocationGeoPoint = new GeoPoint(
                      Number(sublocationData.coordinates.lat),
                      Number(sublocationData.coordinates.lng)
                    );
                    
                    sublocations.push({
                      id: sublocationDoc.id,
                      name: sublocationData.name || `Sublocation ${sublocations.length + 1}`,
                      coordinates: sublocationGeoPoint
                    });
                  } catch (coordError) {
                    console.error(`Error processing sublocation coordinates for site ${siteId}:`, coordError);
                  }
                }
              });
              
              // Update the search document with sublocation data
              if (sublocations.length > 0) {
                searchBetaData.sublocationCount = sublocations.length;
                searchBetaData.mapSublocations = sublocations;
                searchBetaData.zoomLevelData.high.hasSublocations = true;
              }
            }
          } catch (sublocationError) {
            console.error(`Error processing sublocations for site ${siteId}:`, sublocationError);
          }

          // Save the optimized search document
          await setDoc(
            doc(db, 'sitesearch_beta', siteId),
            searchBetaData
          );
          
          stats.success++;
          
          // Update status every 10 sites
          if (stats.processed % 10 === 0 && typeof statusCallback === 'function') {
            statusCallback({
              status: 'processing',
              message: `Processed ${stats.processed} of ${stats.total} sites...`,
              stats,
              lastProcessed: {
                id: siteId,
                name: siteData.name
              }
            });
          }
        } catch (error) {
          stats.error++;
          console.error(`Error processing site ${siteDoc.id}:`, error);
        }
      }
    }
    
    // Final status callback
    if (typeof statusCallback === 'function') {
      statusCallback({
        status: 'completed',
        message: `Migration completed. Processed ${stats.processed} sites with ${stats.success} successes and ${stats.error} errors.`,
        stats
      });
    }
    
    return {
      success: true,
      stats
    };
  } catch (error) {
    console.error('Error in migrateSitesToBeta:', error);
    
    if (typeof statusCallback === 'function') {
      statusCallback({
        status: 'error',
        message: `Migration failed: ${error.message}`,
        error
      });
    }
    
    return {
      success: false,
      error: error.message
    };
  }
};
